import { useCallback, useState } from 'react'
import { Control, FormState, useFieldArray } from 'react-hook-form'
import { View } from 'react-native'
import { useTheme } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'
import { uuidv7 } from 'uuidv7'

import FormFieldDate from '@/components/form/FormFieldDate'
import FormFieldText from '@/components/form/FormFieldText'
import FosterVaccinationItem from '@/components/fosters/cards/vaccines/FosterVaccinationItem'
import { FosterForm } from '@/components/fosters/fosterFormSchema'
import FormFieldOrganizationVaccines from '@/components/organization/cards/formFields/FormFieldOrganizationVaccines'
import Alert from '@/components/shared/Alert'
import InfoText from '@/components/shared/InfoText'
import UniButton from '@/components/unistyles/UniButton'
import UniIcon from '@/components/unistyles/UniIcon'
import UniIconButton from '@/components/unistyles/UniIconButton'
import UniText from '@/components/unistyles/UniText'
import UniTouchableRipple from '@/components/unistyles/UniTouchableRipple'
import MedicalInfoType from '@/enums/medicalInfoType'
import Species from '@/enums/species'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import FosterService from '@/services/fosterService'
import OrganizationService from '@/services/organizationService'
import { useAppStore } from '@/store/useAppStore'
import sharedStyles from '@/styles/styles'
import { Theme } from '@/styles/theme'
import formatDate from '@/utils/formatDate'
import trpc from '@/utils/trpc'

interface Props {
  control: Control<FosterForm, any>
  disabled: boolean
  formState: FormState<FosterForm>
  fosterId: string
}

const FosterVaccinations = ({
  control,
  disabled,
  formState,
  fosterId,
}: Props) => {
  const theme = useTheme<Theme>()
  const { isLargeAndUp } = useBreakpoint()

  const [expandedIndex, setExpandedIndex] = useState<number>(-1)

  const { foster, refreshFoster, isLoading } = FosterService.useFoster(fosterId)

  const currentOrganization = useAppStore.use.currentOrganization().organization

  const vaccinationMedicalInfos = OrganizationService.useMedicalInfo(
    MedicalInfoType.Vaccination,
    foster?.birthDate,
    (foster?.species as Species) || undefined
  )

  const processVaccinationsMutation =
    trpc.foster.processVaccinations.useMutation()

  const { fields, append, remove } = useFieldArray({
    control: control,
    name: 'vaccinations',
    shouldUnregister: false,
    keyName: 'key',
  })

  const handleRemove = useCallback(
    (index: number) => {
      Alert.alert(
        'Delete',
        'Are you sure you want to delete this vaccination?',
        [
          {
            text: 'Cancel',
            onPress: () => console.log('Cancel Pressed'),
            style: 'cancel',
          },
          {
            isPreferred: false,
            text: 'Delete',
            onPress: () => remove(index),
            style: 'destructive',
          },
        ]
      )
    },
    [remove]
  )

  const handleAppend = useCallback(() => {
    append({
      fosterId: fosterId,
      id: uuidv7(),
      vaccineId: '',
      givenBy: null,
      dueDate: null,
      name: null,
    })

    setExpandedIndex(fields.length)
  }, [append, fields.length, fosterId])

  return (
    <View style={styles.root}>
      <InfoText text={vaccinationMedicalInfos?.[0]?.text} />
      {fields.map((field, index) => {
        const vaccination = foster?.vaccinations?.find((v) => v.id === field.id)

        return (
          <View
            key={`${field.key}-vaccinations`}
            style={styles.vaccinationItemContainer}
          >
            <View style={styles.statusIconContainer}>
              {vaccination?.givenDate ? (
                <UniIcon color={'green'} size={24} source="check" />
              ) : (
                <UniIcon
                  color={theme.colors.subtitle}
                  size={24}
                  source="needle"
                />
              )}
            </View>
            <View style={styles.vaccinationFormFieldsContainer}>
              {!isLargeAndUp && (
                <View>
                  <UniTouchableRipple
                    accessibilityRole="button"
                    borderless
                    onPress={() => {
                      setExpandedIndex(index === expandedIndex ? -1 : index)
                    }}
                    style={styles.pressableContainer}
                  >
                    <View style={styles.mobileVaccinationItemContainer}>
                      <UniText>
                        {vaccination?.name}{' '}
                        {vaccination?.givenDate &&
                          `given ${formatDate(vaccination.givenDate)}`}
                        {vaccination?.dueDate &&
                          !vaccination.givenDate &&
                          `due ${formatDate(vaccination.dueDate)}`}
                      </UniText>
                      {vaccination?.givenBy && (
                        <UniText style={styles.clinicTitle}>
                          {vaccination?.givenBy}
                        </UniText>
                      )}
                    </View>
                  </UniTouchableRipple>
                  <FosterVaccinationItem
                    control={control}
                    disabled={disabled}
                    formState={formState}
                    index={index}
                    open={expandedIndex === index}
                    species={foster?.species as Species}
                    vaccination={vaccination}
                  />
                  {expandedIndex === index && (
                    <UniTouchableRipple
                      accessibilityRole="button"
                      borderless
                      onPress={() => setExpandedIndex(-1)}
                      style={styles.vaccinationCollapseContainer}
                    >
                      <UniIcon size={32} source={'chevron-up'} />
                    </UniTouchableRipple>
                  )}
                </View>
              )}
              {isLargeAndUp && (
                <>
                  <FormFieldOrganizationVaccines
                    control={control}
                    disabled={disabled}
                    fieldName={`vaccinations.${index}.vaccineId`}
                    formState={formState}
                    label="Vaccine"
                    species={foster?.species as Species}
                    style={styles.formField}
                  />
                  {!vaccination?.givenDate && (
                    <FormFieldDate
                      control={control}
                      disabled={disabled}
                      errors={formState.errors}
                      fieldName={`vaccinations.${index}.dueDate`}
                      label="Due On"
                      required={false}
                      style={styles.formField}
                    />
                  )}
                  <FormFieldDate
                    control={control}
                    disabled={disabled}
                    errors={formState.errors}
                    fieldName={`vaccinations.${index}.givenDate`}
                    label="Given On"
                    required={false}
                    style={styles.formField}
                  />
                  <FormFieldText
                    control={control}
                    disabled={disabled}
                    errors={formState.errors}
                    fieldName={`vaccinations.${index}.givenBy`}
                    label="Given By"
                    required={true}
                    style={styles.givenBy}
                  />
                </>
              )}
            </View>
            <View style={styles.deleteIconContainer}>
              <UniIconButton
                disabled={disabled}
                icon={'trash-can'}
                onPress={() => handleRemove(index)}
              />
            </View>
          </View>
        )
      })}
      <View style={sharedStyles.buttonRow}>
        <UniButton
          disabled={
            disabled ||
            processVaccinationsMutation.isPending ||
            foster?.birthDate === null
          }
          loading={processVaccinationsMutation.isPending || isLoading}
          mode="contained"
          onPress={() => {
            processVaccinationsMutation.mutate(
              {
                id: fosterId,
                organizationId: currentOrganization?.id || '',
              },
              {
                onSuccess: async () => {
                  await refreshFoster()
                },
              }
            )
          }}
          style={[styles.buttonRowItem, styles.scheduleButton]}
          testID={'process-vaccinations'}
        >
          {!fields.length ? 'Generate Schedule' : 'Update Schedule'}
        </UniButton>
        <UniButton
          disabled={disabled}
          mode="outlined"
          onPress={handleAppend}
          style={styles.buttonRowItem}
        >
          Manually Add Vaccination
        </UniButton>
      </View>
      <View>
        {foster?.birthDate === null && (
          <UniText style={styles.birthDateRequired} variant="bodySmall">
            Please enter a birth date to generate a vaccination schedule.
          </UniText>
        )}
      </View>
    </View>
  )
}

const styles = StyleSheet.create((theme) => {
  return {
    birthDateRequired: {
      marginBottom: theme.tokens.spacing[2],
      marginHorizontal: {
        xs: 'auto',
        md: theme.tokens.spacing[2],
      },
      marginTop: {
        xs: theme.tokens.spacing[3],
        md: theme.tokens.spacing[5],
      },
      textAlign: {
        xs: 'center',
        md: 'left',
      },
      width: '80%',
    },
    clinicTitle: {
      color: theme.colors.subtitle,
      paddingTop: theme.tokens.spacing[1],
    },
    deleteIconContainer: {
      position: {
        xs: 'absolute',
        lg: 'relative',
      },
      right: 0,
      top: 0,
    },
    buttonRowItem: {
      flexBasis: {
        xs: 'auto',
        sm: 0,
      },
      flexGrow: {
        xs: 0,
        sm: 1,
      },
    },
    formField: {
      flex: 0.3,
    },
    givenBy: {
      flex: 0.4,
    },
    mobileVaccinationItemContainer: {
      flex: 1,
      justifyContent: 'center',
      marginLeft: theme.tokens.spacing[8],
    },
    pressableContainer: {
      borderRadius: theme.tokens.containerBorderRadius,
      flex: 1,
      minHeight: 52,
      paddingHorizontal: theme.tokens.spacing[1],
      paddingVertical: theme.tokens.spacing[3],
    },
    root: {
      flex: 1,
    },
    scheduleButton: {
      marginTop: {
        xs: theme.tokens.spacing[3],
        sm: 0,
      },
    },
    statusIconContainer: {
      left: 0,
      paddingHorizontal: 12,
      paddingVertical: 14,
      position: {
        xs: 'absolute',
        lg: 'relative',
      },
      top: 0,
    },
    vaccinationCollapseContainer: {
      alignItems: 'center',
      borderRadius: theme.tokens.containerBorderRadius,
      flexDirection: 'row',
      justifyContent: 'center',
      marginTop: theme.tokens.spacing[3],
      padding: theme.tokens.spacing[2],
    },
    vaccinationFormFieldsContainer: {
      flexDirection: {
        xs: 'column',
        lg: 'row',
      },
      flex: 1,
      gap: 4,
      height: '100%',
    },
    vaccinationItemContainer: {
      alignItems: {
        xs: 'flex-start',
        lg: 'center',
      },
      borderColor: theme.colors.outline,
      borderRadius: theme.tokens.containerBorderRadius,
      borderWidth: {
        xs: 1,
        lg: 0,
      },
      flexDirection: 'row',
      gap: 4,
      marginTop: theme.tokens.spacing[3],
    },
  }
})

export default FosterVaccinations
